import clsx from 'clsx/lite'
import { type GatsbyLinkProps, type Link as GatsbyLink } from 'gatsby'
import React, {
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react'

export type LinkVariant = 'amplification' | 'refresh' | 'legacy'

type LinkPropsBase = {
  className?: string
  children: React.ReactNode
  variant?: LinkVariant
}

type InternalLinkProps = {
  component: typeof GatsbyLink
  to: string
} & Omit<GatsbyLinkProps<any>, 'to'>

type ExternalLinkProps = {
  component: 'a'
  href: string
} & AnchorHTMLAttributes<HTMLAnchorElement>

type ButtonElementProps = {
  component?: 'button'
} & ButtonHTMLAttributes<HTMLButtonElement>

export type LinkProps = LinkPropsBase &
  (InternalLinkProps | ExternalLinkProps | ButtonElementProps)

export const Link = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  LinkProps
>(
  (
    {
      component: Component = 'a',
      children,
      className,
      variant = 'amplification',
      ...props
    },
    outerRef
  ) => {
    const innerRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null)

    useImperativeHandle(outerRef, () => innerRef.current, [])

    return (
      <Component
        ref={innerRef}
        className={clsx(
          'focus-within:outline-border-focus focus-within:outline focus-within:outline-offset-4',
          variant === 'amplification' &&
            'border-b-2 border-solid border-current/10 hover:opacity-70',
          variant === 'refresh' && 'hover:underline',
          variant === 'legacy' && 'text-[#0085AB] no-underline',
          className
        )}
        {...props}
      >
        {children}
      </Component>
    )
  }
)
